import React, { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { DOCUMENT_CATEGORIES } from "@/data/studentData";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { UploadCloud, FileText, Image as ImageIcon, Trash2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

function formatSize(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("Identité");
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/documents");
      setDocs(data);
    } catch {
      toast.error("Chargement impossible");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onFile = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 10 Mo)");
      return;
    }
    // Some browsers/devices don't set file.type for PDFs — we fall back to the extension.
    const name = (file.name || "").toLowerCase();
    const ext = name.split(".").pop() || "";
    const isPdf = file.type === "application/pdf" || ext === "pdf";
    const isImage = (file.type && file.type.startsWith("image/")) || ["jpg","jpeg","png","webp","heic","heif"].includes(ext);
    if (!isPdf && !isImage) {
      toast.error("Format non supporté. Accepté : PDF, JPG, PNG, WEBP, HEIC.");
      return;
    }
    const form = new FormData();
    // Re-wrap as a Blob with explicit content_type so the backend MIME check passes
    // even when the browser failed to set file.type (common on iOS for PDFs).
    const effectiveType = file.type || (isPdf ? "application/pdf" : `image/${ext === "jpg" ? "jpeg" : ext}`);
    const safeFile = new File([file], file.name, { type: effectiveType });
    form.append("file", safeFile);
    setUploading(true);
    try {
      await api.post(`/documents/upload?category=${encodeURIComponent(category)}`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document ajouté");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload échoué");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try {
      await api.delete(`/documents/${id}`);
      setDocs((d) => d.filter((x) => x.id !== id));
      toast.success("Supprimé");
    } catch {
      toast.error("Suppression échouée");
    }
  };

  const handleDownload = async (doc) => {
    try {
      const resp = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: doc.content_type });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Téléchargement échoué");
    }
  };

  const [dragOver, setDragOver] = useState(false);

  return (
    <div className="space-y-8" data-testid="documents-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-indigo-500 font-semibold">Espace sécurisé</p>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 mt-2">Mes documents</h1>
        <p className="text-slate-600 mt-2 max-w-2xl">
          Stocke ici ta CNI, RIB, avis d'imposition, garantie Visale, bail… Stockage privé, accessible uniquement par toi.
        </p>
      </header>

      {/* Uploader */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) onFile(f);
        }}
        className={`rounded-2xl border-2 border-dashed p-8 text-center transition-colors ${
          dragOver ? "border-indigo-400 bg-indigo-50" : "border-slate-200 bg-white"
        }`}
        data-testid="upload-dropzone"
      >
        <div className="w-12 h-12 rounded-2xl bg-indigo-100 text-indigo-600 mx-auto flex items-center justify-center">
          <UploadCloud size={22} />
        </div>
        <p className="font-display text-lg font-semibold mt-3">Glisse un fichier ici</p>
        <p className="text-slate-500 text-sm">ou clique pour sélectionner — <strong>PDF</strong>, JPG, PNG, WEBP, HEIC (max 10 Mo)</p>
        <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
          <div className="w-48">
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="doc-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="bg-indigo-500 hover:bg-indigo-600 h-10"
            data-testid="upload-select-btn"
          >
            {uploading ? <><Loader2 className="animate-spin mr-2" size={16} /> Envoi...</> : "Choisir un fichier"}
          </Button>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
            onChange={(e) => onFile(e.target.files?.[0])}
            className="hidden"
            data-testid="file-input"
          />
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-10 text-slate-500">
          <Loader2 className="animate-spin mr-2" size={18} /> Chargement...
        </div>
      ) : docs.length === 0 ? (
        <div className="text-center py-16 text-slate-400" data-testid="empty-docs">
          <FileText size={40} className="mx-auto" />
          <p className="mt-3">Aucun document pour le moment.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {docs.map((d) => {
            const isImg = d.content_type?.startsWith("image/");
            return (
              <div key={d.id} className="rounded-xl border border-slate-200 bg-white p-5 flex items-start gap-4" data-testid={`doc-card-${d.id}`}>
                <div className="w-11 h-11 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                  {isImg ? <ImageIcon size={20} /> : <FileText size={20} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-slate-900 truncate" title={d.original_filename}>
                    {d.original_filename}
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                    <span className="px-2 py-0.5 rounded-full bg-slate-100">{d.category}</span>
                    <span>{formatSize(d.size)}</span>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => handleDownload(d)}
                      data-testid={`download-${d.id}`}
                      className="text-xs text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
                    >
                      <Download size={12} /> Télécharger
                    </button>
                    <button
                      onClick={() => handleDelete(d.id)}
                      data-testid={`delete-${d.id}`}
                      className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1"
                    >
                      <Trash2 size={12} /> Supprimer
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
